import { useCallback, useEffect, useRef, useState } from 'react'
import type { Translate } from '@webbuilder/i18n'
import { useI18n } from '../../../lib/i18n'
import { longDate } from '../../../lib/dates'
import type { ReactNode } from 'react'
import { ProjectSettingsLayout } from './ProjectSettingsLayout'
import { ConfirmDialog } from '../ConfirmDialog'
import { SubdomainField } from './SubdomainField'
import { LoadingMascot } from '@webbuilder/ui'
import { EmbeddedCheckoutPanel, type CheckoutSummary } from './EmbeddedCheckout'
import { DomainPurchaseSuccess } from './DomainPurchaseSuccess'
import { PUBLIC_SITE_DOMAIN, publicSiteUrl } from '../../../lib/api'
import { DomainSettingsModal } from '../../DashboardSections/DomainSettingsModal'
import {
  listProjectDomains, addProjectDomain, deleteProjectDomain, reconnectProjectDomain, setPrimaryDomain, searchProjectDomains, purchaseDomain,
  listAvailableDomains, listProjects, type AvailableDomain, type Project,
  getSiteSubdomain,
  type CustomDomain, type DomainValidationRecord, type DomainDiagnosis, type BillingPlan, type DomainSearch,
} from '../../../lib/api'
import { IconAlertCircle, IconArrowLeft, IconArrowRight, IconCalendarDollar, IconCheck, IconCircleCheck, IconCopy, IconExternalLink, IconInfoCircle, IconLink, IconLoader2, IconLock, IconRefresh, IconSearch, IconSettings, IconShieldCheck, IconShoppingCart, IconStar, IconUnlink, IconWorld, IconX } from '@tabler/icons-react'
import { can, type ProjectAccessRole } from '@webbuilder/shared-types'

/** Informational blue used across the "connect your domain" steps. */
/**
 * El azul de los pasos de esta pantalla.
 *
 * Es el acento del panel leído en vivo, no un hex: así sigue al color que la
 * persona haya elegido en su menú de usuario. Va como `var()` porque todo esto
 * se pinta en atributos `style`, y ahí una variable CSS vale igual que un color.
 */
const STEP_BLUE = 'var(--dash-accent)'
/** Su versión tenue, para los fondos de aviso. La opacidad la pone el mezclador. */
const stepTint = (percent: number) => `color-mix(in srgb, var(--dash-accent) ${percent}%, transparent)`
const DOMAIN_RECHECK_MIN_MS = 420

/**
 * El precio anual de un dominio.
 *
 * En dólares porque es la moneda en la que cobra el registrador; convertirlo a
 * la moneda local pediría un tipo de cambio del día y un colchón, y un precio
 * que baila cada semana es peor que uno en dólares. Sin decimales: la regla de
 * margen ya redondea al dólar.
 */
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

/** Well-known registrars/DNS providers, for the "where is your domain?" picker. */
type Registrar = { id: string; name: string; color: string; url: string; domain?: string }
const REGISTRARS: Registrar[] = [
  { id: 'porkbun', name: 'Porkbun', color: '#EF6A7C', url: 'https://porkbun.com/account/domainsSpeedy', domain: 'porkbun.com' },
  { id: 'godaddy', name: 'GoDaddy', color: '#00A4A6', url: 'https://dcc.godaddy.com/control/portfolio', domain: 'godaddy.com' },
  { id: 'namecheap', name: 'Namecheap', color: '#FF5100', url: 'https://ap.www.namecheap.com/domains/list/', domain: 'namecheap.com' },
  { id: 'cloudflare', name: 'Cloudflare', color: '#F6821F', url: 'https://dash.cloudflare.com/', domain: 'cloudflare.com' },
  { id: 'netlify', name: 'Netlify', color: '#00AD9F', url: 'https://app.netlify.com/', domain: 'netlify.com' },
  { id: 'squarespace', name: 'Google / Squarespace', color: '#111111', url: 'https://account.squarespace.com/domains', domain: 'squarespace.com' },
  { id: 'hostinger', name: 'Hostinger', color: '#673DE6', url: 'https://hpanel.hostinger.com/', domain: 'hostinger.com' },
  { id: 'wix', name: 'Wix', color: '#0C6EFC', url: 'https://www.wix.com/my-account/sign-in', domain: 'wix.com' },
  /* El único que no es una marca. Se queda como literal igual que sus
     vecinos: una tabla de marcas que dependa del traductor obligaría a
     construirla dentro del componente para nombrar una sola fila. */
  { id: 'other', name: 'Other provider', color: '#71717a', url: '' },
]

/**
 * Deja en el campo solo lo que un dominio admite: letras sin acento, números y
 * guion.
 *
 * Se limpia al teclear y no al enviar porque un dominio no es texto libre: si
 * dejamos escribir «Mi Negocio S.A.» y lo arreglamos por detrás, lo que se
 * busca deja de ser lo que se ve, y el resultado parece un error nuestro. Los
 * acentos se transliteran en vez de borrarse: quien escribe «café» quiere
 * «cafe», no «caf».
 */
function sanitizeDomainName(raw: string): string {
  return (raw
    .toLowerCase()
    /* Pegar un dominio entero es el gesto natural en un buscador de dominios.
       Sin esto, «tienda.com» se quedaba en «tiendacom» —un nombre que nadie
       quiso escribir— en vez de en «tienda». */
    .replace(/^https?:\/\//, '')
    .split('/')[0] ?? '')
    .split('.')[0]!
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]/g, '')
    // Un guion al principio nunca es válido, y dos seguidos están reservados.
    .replace(/^-+/, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 63)
}

/**
 * El resumen que acompaña al formulario de pago de un dominio.
 *
 * Dice desde ya lo que sostiene el modelo entero: el dominio es del cliente y
 * se lo puede llevar. Es lo que genera desconfianza cuando alguien compra un
 * dominio a través de un tercero, y callarlo hasta la letra pequeña no ayuda.
 */
/* Recibe el traductor porque no es un componente: devuelve JSX pero se llama
   desde un manejador, donde no hay hooks. */
function domainSummary(domain: string, priceCents: number, t: Translate): CheckoutSummary {
  const price = priceCents / 100
  return {
    title: domain,
    description: t('publish.checkout.description'),
    features: [
      t('publish.checkout.feature.owned'),
      t('publish.checkout.feature.auto'),
      t('publish.checkout.feature.ssl'),
      t('publish.checkout.feature.notice'),
    ],
    amount: price,
    unit: t('publish.checkout.unit'),
    billed: t('publish.checkout.billed', { price: String(price) }),
    backLabel: t('publish.checkout.back'),
    legal: (
      <>
              {t('publish.buyTerms')}{' '}
        <a href="/terminos" target="_blank" rel="noreferrer">{t('publish.terms')}</a>{' '}
              {t('publish.and')}{' '}
        <a href="/privacidad" target="_blank" rel="noreferrer">{t('publish.privacy')}</a>.
              {t('publish.oneYearNotice')}
      </>
    ),
  }
}

/** Pull the human message out of a thrown `request()` error ("402: {json}"). */
function errorMessage(err: unknown, t: Translate): string {
  const raw = err instanceof Error ? err.message : String(err)
  const brace = raw.indexOf('{')
  if (brace >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(brace)) as { error?: string }
      if (parsed.error) return parsed.error
    } catch { /* not JSON */ }
  }
  return t('publish.genericError')
}

/** DNS record "name" (host) the user types at their registrar, from the full hostname. */
function recordName(hostname: string): string {
  const labels = hostname.split('.')
  // Approximation: registrable domain = last two labels (fine for .com/.mx/.io).
  if (labels.length <= 2) return '@'
  return labels.slice(0, labels.length - 2).join('.')
}

function isApex(hostname: string): boolean {
  return recordName(hostname) === '@'
}

/** The DNS records to show for a domain: the routing CNAME + any validation TXT. */
function dnsRecords(domain: CustomDomain): DomainValidationRecord[] {
  const routing: DomainValidationRecord = { type: 'CNAME', name: recordName(domain.hostname), value: domain.cnameTarget }
  return [routing, ...(domain.verification.records ?? [])]
}

function CopyButton({ value }: { value: string }) {
  const { t } = useI18n()
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | null>(null)
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])
  return (
    <button
      type="button"
      className="dash-secondary-btn"
      style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
      title={t('publish.copy')}
      onClick={() => {
        void navigator.clipboard?.writeText(value)
        setCopied(true)
        if (timer.current) window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? <IconCheck size={13} /> : <IconCopy size={13} />}
    </button>
  )
}

const STATUS_META: Record<CustomDomain['status'], { color: string; bg: string }> = {
  purchasing: { color: STEP_BLUE, bg: stepTint(10) },
  pending_dns: { color: STEP_BLUE, bg: stepTint(10) },
  verifying: { color: STEP_BLUE, bg: stepTint(10) },
  active: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
  error: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
  /* Comprado y suyo, pero sin apuntar a ningún sitio. En gris y no en rojo: no
     hay nada roto, sólo está sin usar.

     Sin el «Tuyo ·» que llevaba delante: la fila está en SU lista de
     direcciones, así que de quién es ya se sabía antes de leer el sello. Lo
     único que aporta es lo otro — que no está conectado. */
  disconnected: { color: 'var(--dash-muted)', bg: 'transparent' },
}

/* El servidor manda el estado como texto y el mapa de arriba es exhaustivo sólo
   frente al tipo de HOY: un estado nuevo en el worker llegaría a un panel viejo
   y `meta.color` reventaría el render entero —pantalla en blanco, no una
   etiqueta fea—. Ante la duda se pinta «Procesando…». */
const FALLBACK_STATUS_META = { color: STEP_BLUE, bg: stepTint(10) }

/* El rótulo va aparte del color porque se traduce, y este mapa es de módulo:
   aquí no hay hooks, así que el traductor entra por parámetro. El `default`
   cubre lo mismo que `FALLBACK_STATUS_META` — un estado que el worker aprenda
   a mandar antes de que este panel lo conozca. */
function statusLabel(status: CustomDomain['status'], t: Translate): string {
  switch (status) {
    case 'purchasing': return t('publish.status.purchasing')
    case 'pending_dns': return t('publish.status.pendingDns')
    case 'verifying': return t('publish.status.verifying')
    case 'active': return t('publish.status.active')
    case 'error': return t('publish.status.error')
    case 'disconnected': return t('publish.status.disconnected')
    default: return t('publish.status.processing')
  }
}

/** «22 de agosto de 2027»: la fecha de un vencimiento se lee entera, sin abreviar. */
function renewalDate(iso: string): string {
  return longDate(iso)
}

/**
 * Dónde se pagan las renovaciones.
 *
 * Facturación es otra vista del mismo panel, así que la dirección se arma con la
 * de ahora y su parámetro: escribirla a mano dejaría un enlace que se rompe en
 * local y en cualquier despliegue que no cuelgue de la raíz. Va detrás de la
 * palabra y no escrita a la vista: una dirección con su interrogante y su
 * parámetro en mitad de una frase se lee como un error de la pantalla.
 */
function billingHref(): string {
  return `${window.location.pathname}?view=billing`
}

/** La pantalla de la cuenta: todos los dominios y en qué sitio está cada uno. */
function domainsHref(): string {
  return `${window.location.pathname}?view=domains`
}

/** Small numbered circle for the step-by-step guide. */
function StepNumber({ n }: { n: number }) {
  return (
    <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: STEP_BLUE, color: '#fff', fontSize: 12, fontWeight: 700, display: 'grid', placeItems: 'center' }}>{n}</span>
  )
}

/** Brand monogram badge — fallback when a logo can't load. */
function Monogram({ name, color }: { name: string; color: string }) {
  return (
    <span style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 9, background: color, color: '#fff', fontSize: 16, fontWeight: 700, display: 'grid', placeItems: 'center' }}>
      {name.charAt(0)}
    </span>
  )
}

/**
 * Minimalist brand mark: the provider's own favicon (served by DuckDuckGo's icon
 * service), on a clean white tile. Falls back to a colored monogram if it fails
 * to load — so we never embed brand assets, just reference each site's own icon.
 */
function BrandLogo({ name, color, domain }: { name: string; color: string; domain?: string | undefined }) {
  const [failed, setFailed] = useState(false)
  if (!domain || failed) return <Monogram name={name} color={color} />
  return (
    <img
      src={`https://icons.duckduckgo.com/ip3/${domain}.ico`}
      alt=""
      width={36}
      height={36}
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 9, objectFit: 'contain', background: '#fff', padding: 5, boxSizing: 'border-box', border: '1px solid var(--dash-border)' }}
    />
  )
}

/** Colored dot/box shared by the diagnosis banner tones. */
const DIAGNOSIS_TONE: Record<DomainDiagnosis['state'], { color: string; bg: string }> = {
  not_found: { color: STEP_BLUE, bg: stepTint(8) },
  wrong_target: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)' },
  dns_ok: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.08)' },
}

/** Precise, actionable DNS status shown on a pending domain. */
function DiagnosisBanner({ diagnosis, cnameTarget, standalone = false }: { diagnosis: DomainDiagnosis; cnameTarget: string; standalone?: boolean }) {
  const { t } = useI18n()
  const tone = DIAGNOSIS_TONE[diagnosis.state]
  let title = ''
  let body: ReactNode = null
  if (diagnosis.state === 'dns_ok') {
    title = t('publish.diagnosis.ok')
    body = <>{t('publish.dns.pointing')}</>
  } else if (diagnosis.state === 'wrong_target') {
    title = t('publish.diagnosis.wrongTarget')
    body = <>{t('publish.dns.wrongTarget', { found: diagnosis.foundTarget ?? '', expected: cnameTarget })}</>
  } else {
    title = t('publish.diagnosis.notFound')
    body = diagnosis.provider
      ? <>{t('publish.dns.managedBy', { provider: diagnosis.provider })}</>
      : <>{t('publish.dns.notFound')}</>
  }
  return (
    <div style={{ display: 'flex', gap: 10, padding: '12px 14px', borderRadius: 10, background: tone.bg, border: `1px solid ${tone.color}33`, marginBottom: standalone ? 0 : 16 }}>
      {diagnosis.state === 'dns_ok'
        ? <IconShieldCheck size={17} color={tone.color} style={{ flexShrink: 0, marginTop: 1 }} />
        : <IconAlertCircle size={17} color={tone.color} style={{ flexShrink: 0, marginTop: 1 }} />}
      <div style={{ minWidth: 0 }}>
        <div className="dash-card-primary" style={{ fontWeight: 600, color: tone.color, marginBottom: 2 }}>{title}</div>
        <p className="dash-card-description" style={{ margin: 0 }}>{body}</p>
      </div>
    </div>
  )
}

function DnsRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="dash-dns-row">
      <span className="dash-card-caption dash-dns-row-label">{label}</span>
      <span className={`dash-card-meta dash-dns-row-value${mono ? ' is-mono' : ''}`}>{value}</span>
      <CopyButton value={value} />
    </div>
  )
}

type ConfirmState =
  | { kind: 'cancel' }
  | { kind: 'disconnect'; domain: CustomDomain }
  | null

export function PublishSettings({ projectId, token, projectName, plan, publishedSubdomain, projectRole = 'owner', onOpenPlans, onBack }: {
  projectId: string
  token: string
  projectName: string
  plan: BillingPlan
  publishedSubdomain: string | null
  /**
   * Qué eres en el proyecto.
   *
   * Esta pantalla la abre cualquiera con `project.settings` —el rol de Editor
   * incluido— pero el bloque del dominio propio pide `domains.manage`. Por
   * defecto `owner` para no esconderlo a quien construya el componente sin
   * pasarlo.
   */
  projectRole?: ProjectAccessRole
  onOpenPlans: () => void
  /** Al editor, que es donde se construyen las páginas al publicar. */
  onOpenEditor?: (() => void) | undefined
  /** Para que quien tiene el estado del proyecto se entere de que ya no está. */
  onUnpublished?: (() => void) | undefined
  onBack?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const canManageDomains = can(projectRole, 'domains.manage')
  const [domains, setDomains] = useState<CustomDomain[]>([])
  /* Sin Cloudflare de verdad detrás: los dominios se conectan al instante y no
     resuelven a nada. Se avisa; un «activo» que no lo está es peor que un error. */
  const [mockHostnames, setMockHostnames] = useState(false)
  const [cnameTarget, setCnameTarget] = useState('cname.veltica.com')
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  // Wizard: 0 = closed, 1 = enter domain, 2 = pick registrar, 3 = DNS + connect.
  /**
   * Por dónde se está consiguiendo un dominio, si es que se está.
   *
   * Uno o el otro, nunca los dos: o se conecta el que ya se tiene, o se busca
   * uno nuevo. Con los dos formularios abiertos a la vez hay que leer los dos
   * para saber en cuál se está, y eso en una pantalla que se abre para una sola
   * decisión.
   *
   * Cerrado por defecto, que es el otro cambio: el buscador vivía arriba del
   * todo, siempre, así que quien entra a mirar la dirección de su sitio —que es
   * a lo que se entra aquí— se encontraba un escaparate encima y lo suyo debajo.
   */
  /* Arranca en «buscar». Es lo que la mayoría necesita al llegar aquí: quien
     ya tiene dominio lo conecta una vez y no vuelve; quien no lo tiene entra
     precisamente a eso. Cambiar de camino es un botón en el pie. */
  const [shopMode, setShopMode] = useState<'connect' | 'search'>('search')
  const [wizardStep, setWizardStep] = useState<0 | 1 | 2 | 3>(0)
  const [input, setInput] = useState('')
  const [registrarId, setRegistrarId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [refreshingId, setRefreshingId] = useState<string | null>(null)
  const [settingPrimary, setSettingPrimary] = useState(false)
  const [confirm, setConfirm] = useState<ConfirmState>(null)
  // Buscador. Nada de buscar mientras se teclea: el registrador limita la
  // consulta de disponibilidad a una cada diez segundos para toda la cuenta,
  // así que se busca al enviar y no en cada tecla.
  const [query, setQuery] = useState('')
  const domainSearchInputRef = useRef<HTMLInputElement>(null)
  const [searching, setSearching] = useState(false)
  const [pendingDomain, setPendingDomain] = useState<string | null>(null)
  const [results, setResults] = useState<DomainSearch | null>(null)
  const [searchError, setSearchError] = useState<string | null>(null)
  // La compra en curso. `null` = no hay pago abierto.
  const [checkout, setCheckout] = useState<{ clientSecret: string; summary: CheckoutSummary; domain: string } | null>(null)
  const [buying, setBuying] = useState<string | null>(null)
  /* El dominio recién pagado, mientras se enseña la pantalla de gracias. El
     registro lo hace el webhook y tarda unos segundos: sin esto se volvía a una
     lista donde el dominio todavía no estaba. */
  const [justBought, setJustBought] = useState<string | null>(null)

  const isFree = plan === 'free'
  /**
   * La dirección elegida, que puede no ser la publicada.
   *
   * Se pide al servidor en vez de derivarla del nombre del proyecto: desde que
   * se puede cambiar, el nombre ya no la decide. Mientras no llega se sigue
   * enseñando la propuesta de siempre, para no dejar el hueco en blanco.
   */
  const [chosenSubdomain, setChosenSubdomain] = useState<string | null>(publishedSubdomain)
  useEffect(() => {
    let alive = true
    getSiteSubdomain(token, projectId)
      .then((data) => { if (alive && data.subdomain) setChosenSubdomain(data.subdomain) })
      .catch(() => {})
    return () => { alive = false }
  }, [projectId, token])

  const suggestedSubdomain = projectName.toLowerCase().replace(/\s+/g, '-') || 'tu-sitio'
  const subdomainLabel = chosenSubdomain ?? suggestedSubdomain
  const velticaDomain = `${subdomainLabel}.${PUBLIC_SITE_DOMAIN}`
  const velticaUrl = publishedSubdomain ? publicSiteUrl(publishedSubdomain) : `https://${velticaDomain}`
  const isPublished = Boolean(publishedSubdomain)

  const load = useCallback(async () => {
    try {
      const data = await listProjectDomains(token, projectId)
      setDomains(data.domains)
      setMockHostnames(data.mock)
      if (data.cnameTarget) setCnameTarget(data.cnameTarget)
    } catch {
      /* keep whatever we had */
    } finally {
      setLoading(false)
    }
  }, [token, projectId])

  useEffect(() => { void load() }, [load])

  // Poll while any domain is still propagating/verifying, so the UI advances to
  // "SSL activo" on its own without a manual refresh.
  /* `purchasing` también cuenta: es el estado más corto de todos y el único que
     avanza solo sin que el usuario haga nada, así que es justo el que no puede
     quedarse quieto en pantalla. */
  const anyPending = domains.some((d) => d.status === 'purchasing' || d.status === 'pending_dns' || d.status === 'verifying')
  useEffect(() => {
    if (!anyPending) return
    const timer = window.setInterval(() => { void load() }, 8000)
    return () => window.clearInterval(timer)
  }, [anyPending, load])

  const search = async (raw: string) => {
    const term = raw.trim()
    if (term.length < 2 || searching) return
    const replacingResult = results !== null
    const startedAt = performance.now()
    setPendingDomain(term.includes('.') ? term : `${term}.com`)
    setSearching(true)
    setSearchError(null)
    try {
      const nextResults = await searchProjectDomains(token, projectId, term)
      if (replacingResult) {
        const remaining = DOMAIN_RECHECK_MIN_MS - (performance.now() - startedAt)
        if (remaining > 0) await new Promise((resolve) => window.setTimeout(resolve, remaining))
      }
      setResults(nextResults)
    } catch (err) {
      setResults(null)
      setSearchError(errorMessage(err, t))
    } finally {
      setSearching(false)
      setPendingDomain(null)
    }
  }

  const buy = async (domain: string) => {
    if (buying) return
    setBuying(domain)
    setSearchError(null)
    try {
      const result = await purchaseDomain(token, projectId, domain)
      /* Sin secreto no hay nada que cobrar: el crédito cubrió el dominio
         entero. Se recarga la lista, que es donde ya aparece registrándose, en
         vez de abrir una pantalla de pago de cero dólares. */
      if (!result.clientSecret) {
        await load()
        /* A medias no es «no se compró»: el dominio ya es del cliente y lo que
           faltó fue terminar de conectarlo. Se dice así, y la fila que acaba de
           aparecer en la lista lleva su estado real y el aviso de escribirnos.
           Lo que NO se hace es invitar a comprar otro. */
        if (result.incomplete) {
          setSearchError(t('publish.purchaseIncomplete'))
        } else {
          setJustBought(domain)
        }
        return
      }
      setCheckout({ clientSecret: result.clientSecret, summary: domainSummary(domain, result.firstYearCents, t), domain })
    } catch (err) {
      setSearchError(errorMessage(err, t))
    } finally {
      setBuying(null)
    }
  }

  /* El asistente se abre vacío desde «¿ya tienes uno?», y con el nombre puesto
     cuando se llega desde un resultado que ya estaba registrado. */
  const openWizard = (hostname = '') => { setWizardStep(1); setInput(hostname); setRegistrarId(null); setError(null) }
  /* Al terminar de conectar, de vuelta al estado de entrada. */
  const closeWizard = () => { setWizardStep(0); setInput(''); setRegistrarId(null); setError(null); setShopMode('search') }
  /* «Empezar de nuevo» no cambia de camino: vacía lo escrito y vuelve al primer
     paso del asistente, que es donde estaba antes de escribir nada. */
  const restartWizard = () => { setInput(''); setRegistrarId(null); setError(null); setWizardStep(1) }
  /* Cada camino cierra al otro: abrir el buscador cancela el asistente y
     viceversa, para que nunca haya dos formularios pidiendo lo mismo. */
  const openShop = () => { setShopMode('search'); setWizardStep(0) }
  const openConnect = () => { setShopMode('connect'); openWizard() }
  // Cancel: confirm only when there's progress worth losing.
  const requestCancel = () => {
    if (input.trim() || registrarId) setConfirm({ kind: 'cancel' })
    else restartWizard()
  }

  // Step 1 → 2: basic client-side check (a real FQDN with a dot) before continuing.
  const goToRegistrar = () => {
    const hostname = input.trim().toLowerCase()
    if (!/^([a-z0-9-]+\.)+[a-z]{2,}$/.test(hostname)) {
      setError(t('publish.invalidDomain'))
      return
    }
    setError(null)
    setWizardStep(2)
  }

  // Step 3 (connect): create the Cloudflare Custom Hostname.
  const submit = async () => {
    const hostname = input.trim().toLowerCase()
    if (!hostname || adding) return
    setAdding(true)
    setError(null)
    try {
      const created = await addProjectDomain(token, projectId, hostname)
      setDomains((current) => [...current.filter((d) => d.id !== created.id), created])
      closeWizard()
    } catch (err) {
      setError(errorMessage(err, t))
    } finally {
      setAdding(false)
    }
  }

  const retry = async (domainId: string) => {
    setRefreshingId(domainId)
    await load()
    setRefreshingId(null)
  }

  const makePrimary = async (domainId: string | null) => {
    if (settingPrimary) return
    setSettingPrimary(true)
    setError(null)
    try {
      await setPrimaryDomain(token, projectId, domainId)
      await load()
    } catch (err) {
      setError(errorMessage(err, t))
    } finally {
      setSettingPrimary(false)
    }
  }

  const hasPrimaryCustom = domains.some((d) => d.isPrimary)

  /**
   * Reconectar uno comprado aquí: una llamada y ya.
   *
   * Antes esto abría el asistente de dominios externos con el nombre puesto, o
   * sea que le pedía al cliente pegar registros DNS a mano — justo el trabajo
   * que comprar el dominio con nosotros existía para ahorrarle. Su DNS vive en
   * nuestra cuenta del registrador; lo apuntamos nosotros.
   */
  /* Los pagados y sin usar. Se cargan aparte de la lista del proyecto porque son
     de la cuenta entera, no de este sitio. */
  const [available, setAvailable] = useState<AvailableDomain[]>([])
  /* Los sitios a los que se puede mandar un dominio suelto. Los compartidos
     fuera: ahí no se tienen los permisos de dominio y conectarlo daría un 403. */
  const [myProjects, setMyProjects] = useState<Project[]>([])
  /* A dónde va cada uno mientras no se confirma. Por defecto, aquí. */
  const [target, setTarget] = useState<Record<string, string>>({})

  const loadAvailable = useCallback(async () => {
    if (!canManageDomains) return
    try {
      const [list, projects] = await Promise.all([
        listAvailableDomains(token, projectId),
        listProjects(token),
      ])
      setAvailable(list)
      setMyProjects(projects.filter((project) => !project.shared))
    } catch {
      /* Sin bloque, y sin ruido: es una comodidad, no la pantalla. Quien no la
         vea sigue teniendo el asistente y la compra. */
    }
  }, [canManageDomains, token, projectId])

  useEffect(() => { void loadAvailable() }, [loadAvailable])

  /* La ventana de ajustes del dominio, la misma de la pantalla de la cuenta. */
  const [openSettings, setOpenSettings] = useState<CustomDomain | null>(null)

  const [reconnecting, setReconnecting] = useState<string | null>(null)

  /**
   * Trae aquí un dominio que estaba suelto.
   *
   * Es el mismo `reconnect` del servidor: reconectar y adoptar son la misma
   * operación, y la única diferencia está en de dónde salió el id. Por eso no
   * hay un endpoint aparte — lo que cambió fue que el servidor dejara de exigir
   * que el dominio viniera de ESTE proyecto.
   */
  const adopt = async (domainId: string, toProjectId: string) => {
    if (reconnecting) return
    setReconnecting(domainId)
    setError(null)
    try {
      const connected = await reconnectProjectDomain(token, toProjectId, domainId)
      /* Sólo entra en la lista de arriba si se quedó AQUÍ. Mandado a otro
         sitio, desaparece de los disponibles y ya está: enseñarlo entre las
         direcciones de este proyecto sería mentir sobre dónde acabó. */
      if (toProjectId === projectId) {
        setDomains((current) => [...current.filter((d) => d.id !== connected.id), connected])
      }
      setAvailable((current) => current.filter((item) => item.id !== domainId))
    } catch (err) {
      setError(errorMessage(err, t))
    } finally {
      setReconnecting(null)
    }
  }

  const reconnect = async (domain: CustomDomain) => {
    if (reconnecting) return
    setReconnecting(domain.id)
    setError(null)
    try {
      const updated = await reconnectProjectDomain(token, projectId, domain.id)
      setDomains((current) => current.map((d) => (d.id === updated.id ? updated : d)))
    } catch (err) {
      setError(errorMessage(err, t))
    } finally {
      setReconnecting(null)
    }
  }

  const disconnect = async (domain: CustomDomain) => {
    const previous = domains
    /**
     * Uno comprado aquí NO desaparece de la lista: se queda suelto.
     *
     * El servidor ya lo hace así —la fila es la prueba de la compra y su fecha
     * de renovación—, pero aquí se quitaba de la lista igualmente, así que
     * parecía borrado hasta que alguien recargaba y volvía a aparecer. Ver un
     * dominio que pagaste desvanecerse es exactamente el susto que no debe dar.
     *
     * Se decide con lo que ya se sabe de la fila, sin esperar a la respuesta:
     * así no hay ni parpadeo ni un estado intermedio en el que no está.
     */
    const kept = Boolean(domain.registrar)
    setDomains((current) => kept
      ? current.map((d) => d.id === domain.id
        ? { ...d, status: 'disconnected' as const, isPrimary: false, sslStatus: null }
        : d)
      : current.filter((d) => d.id !== domain.id))
    try {
      await deleteProjectDomain(token, projectId, domain.id)
    } catch (err) {
      setDomains(previous)
      setError(errorMessage(err, t))
    }
  }

  /* Dos caracteres como mínimo, y ni empezar ni acabar en guion — que es lo
     que aceptaría el registrador. El campo ya impide lo demás. */
  const canSearch = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(query)

  const selected = results?.selected ?? null
  /* Mientras el asistente está abierto los resultados se ocultan: son dos
     caminos distintos y apilarlos deja la pantalla sin un siguiente paso claro. */
  const showResults = wizardStep === 0 && (results !== null || searchError !== null)

  const registrar = REGISTRARS.find((r) => r.id === registrarId) ?? null
  const previewHostname = input.trim().toLowerCase()
  const previewRecord: DomainValidationRecord = {
    type: isApex(previewHostname) ? 'CNAME / ALIAS' : 'CNAME',
    name: recordName(previewHostname),
    value: cnameTarget,
  }

  return (
    <ProjectSettingsLayout
      title={t('publish.title')}
      description={t('publish.description')}
      onBack={onBack}
      stacked
    >
      {/* Error fuera del asistente (p. ej. fallo al desconectar) */}
      {error && wizardStep === 0 ? (
        <div className="dash-status-label" role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '10px 14px', borderRadius: 9 }}>
          <IconAlertCircle size={15} /> {error}
        </div>
      ) : null}


      {/* El buscador va antes que las direcciones activas. Se mantienen juntos
          en este grupo para que los estados posteriores de compra y conexión
          sigan apareciendo debajo de ambos bloques. */}
      <div className="dash-domain-sections">
      {/* ── Las direcciones que tiene el sitio ────────────────────────────
          Una sección con su rótulo, y cada dirección una tarjeta suya. Antes
          eran filas dentro de una tarjeta grande con fondo y sombra; en el
          panel, el contenido de un widget va sin fondo y con un filo claro, y
          esto es contenido, no un panel dentro de otro. */}
      <section className="dash-domain-group" aria-label={t('publish.active.aria')}>
        {/* La misma anatomía que un widget del dashboard: cabecera de 74px con
            icono glass y cuerpo con 18px de aire. Así el bloque se reconoce
            como una tarjeta principal, no como una lista metida en una caja. */}
        <div className="dash-domain-panel dash-domain-panel--addresses">
        <div className="dash-domain-panel-head">
          <div className="dash-overview-summary-head-main">
            <span className="dash-widget-glass-icon" aria-hidden="true"><IconWorld size={18} /></span>
            <h3 className="dash-card-title">{t('publish.active.title')}</h3>
          </div>
          <a className="dash-secondary-btn dash-summary-action dash-domain-panel-action" href={domainsHref()}>
            <span>{t('publish.viewAllDomains')}</span>
            <IconArrowRight size={13} aria-hidden="true" />
          </a>
        </div>
        <div className="dash-domain-panel-body">
        {/* En el mismo sitio donde se leen las direcciones, no al pie: quien
            está mirando un «SSL activo» de mentira tiene que verlo ahí. */}
        {mockHostnames ? (
          <p className="dash-domain-mock-note">
            <IconInfoCircle size={14} aria-hidden="true" />
              {t('publish.testMode')}
          </p>
        ) : null}
        <article className="dash-domain-card dash-domain-card--subdomain">
          <SubdomainField
            projectId={projectId}
            token={token}
            current={subdomainLabel}
            publicDomain={PUBLIC_SITE_DOMAIN}
            {...(isPublished ? { href: velticaUrl } : {})}
            onSaved={(next) => setChosenSubdomain(next)}
          />
          {/* Dos grupos, uno en cada punta: a la izquierda EN QUÉ ESTADO está la
              dirección —lo que se viene a mirar—, y a la derecha qué es y quién
              manda. «Principal» y «Hacer principal» se quedan al final del
              renglón, bajo «Cambiar dirección»: las acciones de esta tarjeta
              caen todas sobre el mismo borde.

              «Activo» va en verde y delante: es el sí o el no de la dirección.
              «SSL activo» lo acompaña en azul, con el detalle de que además va
              cifrada — el mismo par que llevan los dominios propios. */}
          <div className="dash-domain-card-meta">
            {isPublished ? (
              <span className="dash-domain-card-badges dash-domain-card-statuses">
                <span className="dash-domain-badge"><IconCircleCheck size={13} /> {t('publish.badge.active')}</span>
                <span className="dash-domain-badge is-secure"><IconShieldCheck size={13} /> {t('publish.badge.ssl')}</span>
              </span>
            ) : null}
            <span className="dash-domain-card-badges dash-domain-card-primary">
              <span className="dash-domain-card-state">
                {!isPublished
                  ? t('publish.subdomain.unpublished')
                  : hasPrimaryCustom
                    ? t('publish.subdomain.includedRedirect')
                    : t('publish.subdomain.included')}
              </span>
              {!isPublished ? null : !hasPrimaryCustom ? (
                <span className="dash-domain-badge is-primary"><IconStar size={12} /> {t('publish.badge.primary')}</span>
              ) : (
                /* El mismo botón con marco que «Cambiar dirección»: hacer
                   principal mueve a dónde apunta el sitio, no es un enlace de
                   paso. Ver `.dash-domain-card-action`. */
                <button className="dash-domain-card-action" type="button" onClick={() => void makePrimary(null)} disabled={settingPrimary}>
                  <IconStar size={13} aria-hidden="true" /> {t('publish.makePrimary')}
                </button>
              )}
            </span>
          </div>
        </article>

        {loading ? (
          <p className="dash-card-description dash-domain-addresses-note" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconLoader2 size={14} className="dash-spin" /> {t('publish.loadingDomains')}
          </p>
        ) : domains.map((domain) => {
          const meta = STATUS_META[domain.status] ?? FALLBACK_STATUS_META
          /* Un dominio suelto no está «a medio configurar»: está tuyo y sin
             usar. Sin esta salvedad se le pintaban los pasos de DNS y el aviso
             de completar la conexión, invitando a arreglar algo que no falla. */
          const needsSetup = domain.status !== 'active' && domain.status !== 'disconnected'
          /* Lo compró aquí: su DNS vive en nuestra cuenta y ya lo apuntamos
             nosotros. Enseñarle los registros a pegar a mano sería pedirle
             justo lo que la compra existía para ahorrarle. */
          const selfManaged = Boolean(domain.registrar) || domain.status === 'purchasing'
          return (
            <article key={domain.id} className="dash-domain-card">
              <div className="dash-domain-card-line">
                {domain.status === 'active' ? (
                  <a className="dash-domain-card-name" href={`https://${domain.hostname}`} target="_blank" rel="noreferrer">
                    {domain.hostname} <IconExternalLink size={13} aria-hidden="true" />
                  </a>
                ) : (
                  <span className="dash-domain-card-name">{domain.hostname}</span>
                )}
                {domain.status === 'disconnected' ? (
                  <button
                    className="dash-domain-card-change"
                    type="button"
                    disabled={reconnecting === domain.id}
                    onClick={() => void reconnect(domain)}
                  >
                    {/* El mismo círculo giratorio que el resto de las esperas
                        cortas del panel —«Buscando…», «Comprobando…»—: reconectar
                        habla con el registrador y con Cloudflare, así que tarda
                        lo suyo y sin señal parece que el clic no hizo nada. */}
                    {reconnecting === domain.id
                      ? <><IconLoader2 size={13} className="dash-spin" /> {t('publish.reconnecting')}</>
                      : <><IconLink size={13} aria-hidden="true" /> {t('publish.reconnect')}</>}
                  </button>
                ) : domain.status === 'purchasing' ? null : (
                  <>
                    {/* Su DNS y su transferencia, en la misma ventana que abre la
                        pantalla de Dominios de la cuenta. Faltaba: desde aquí, un
                        dominio conectado no tenía ningún camino a su DNS — había
                        que saber que existía otra pantalla y salir a buscarla. */}
                    {domain.registrar ? (
                      <button
                        className="dash-domain-card-change"
                        type="button"
                        onClick={() => { setOpenSettings(domain) }}
                      >
                        <IconSettings size={13} aria-hidden="true" /> {t('publish.dnsAndTransfer')}
                      </button>
                    ) : null}
                    <button className="dash-domain-card-change" type="button" onClick={() => setConfirm({ kind: 'disconnect', domain })}>
                    {/* El eslabón roto y el eslabón entero: desconectar y
                        reconectar son la misma acción en los dos sentidos, y con
                        iconos de familias distintas no se leerían como pareja. */}
                      <IconUnlink size={13} aria-hidden="true" /> {t('publish.disconnect')}
                    </button>
                  </>
                )}
              </div>

              <div className="dash-domain-card-meta">
                {/* Sello para lo que un dominio ES, texto para lo que le está
                    pasando.

                    Activo y sin conectar son sitios donde un dominio se queda, y
                    llevan los mismos sellos que la fila de véltica: el mismo
                    hecho pintado distinto en cada fila parecían dos estados
                    distintos. «Esperando DNS» o «revisa la configuración» no son
                    sellos que se ganen —son pasos, y se van solos—, así que se
                    quedan en texto con su rueda y su color. */}
                {domain.status === 'active' ? (
                  /* El mismo par que la fila de véltica: verde el estado, azul
                     el certificado. Son la misma pregunta sobre dos direcciones
                     y tienen que contestarse igual. */
                  <span className="dash-domain-card-badges">
                    <span className="dash-domain-badge"><IconCircleCheck size={13} /> {t('publish.badge.active')}</span>
                    <span className="dash-domain-badge is-secure"><IconShieldCheck size={13} /> {statusLabel(domain.status, t)}</span>
                  </span>
                ) : domain.status === 'disconnected' ? (
                  /* También un sello, en gris: «sin conectar» es un estado en el
                     que un dominio se queda —no es un paso que esté corriendo—
                     y como texto suelto junto a los sellos de las demás filas
                     parecía que a ésta le faltaba algo por cargar. */
                  <span className="dash-domain-badge is-idle"><IconWorld size={13} /> {statusLabel(domain.status, t)}</span>
                ) : (
                  /* Los estados de paso siguen siendo texto con su rueda: están
                     pasando ahora mismo y se van solos. */
                  <span className="dash-domain-card-state" style={{ color: meta.color }}>
                    <IconRefresh size={11} className="dash-spin" />
                    {' '}{statusLabel(domain.status, t)}
                  </span>
                )}
                {domain.status !== 'active' ? null : (
                  <span className="dash-domain-card-badges">
                    {domain.isPrimary ? (
                      <span className="dash-domain-badge is-primary"><IconStar size={12} /> {t('publish.badge.primary')}</span>
                    ) : (
                      <button className="dash-domain-card-action" type="button" onClick={() => void makePrimary(domain.id)} disabled={settingPrimary}>
                        <IconStar size={13} aria-hidden="true" /> {t('publish.makePrimary')}
                      </button>
                    )}
                  </span>
                )}
              </div>

              {/* Cuándo vence y quién lo cobra.
                  Sólo en los comprados aquí: de uno traído de fuera no sabemos
                  su fecha ni le cobramos la renovación —eso es entre el cliente
                  y su registrador—, y ponerle una fecha nuestra sería mentirle
                  sobre quién le está guardando el dominio.

                  Con la fecha va SIEMPRE dónde se paga. Un vencimiento que no
                  dice qué hacer al respecto es sólo una fecha inquietante, y el
                  cobro del dominio va por su cuenta —aniversario propio, factura
                  propia—, así que no se deduce de la membresía. */}
              {domain.registrar && domain.expiresAt ? (
                <p className="dash-domain-card-renewal">
                  <IconCalendarDollar size={14} aria-hidden="true" />
                      <span>
                        {/* La frase entera con dos huecos: la fecha y el enlace.
                            Partirla la haría intraducible — en inglés el enlace
                            no cae donde cae en español. */}
                        {/* Marcadores del área de uso privado y no de control: `\u0000` en una
                            expresión regular es un carácter de control, y el lint lo
                            rechaza con razón — cuesta verlo al leer el código. */}
                        {t('publish.renewal', { date: '\uE000', link: '\uE001' })
                          .split(/([\uE000\uE001])/)
                          .map((chunk, index) => {
                            if (chunk === '\uE000') return <strong key={index}>{renewalDate(domain.expiresAt ?? '')}</strong>
                            if (chunk === '\uE001') return <a key={index} href={billingHref()}>{t('publish.billing')}</a>
                            return chunk
                          })}
                  </span>
                </p>
              ) : null}

              {needsSetup && selfManaged ? (
                <div className="dash-domain-step" style={{ background: stepTint(6), borderTop: `1px solid ${stepTint(18)}`, display: 'flex', gap: 12 }}>
                  <IconInfoCircle size={18} color={STEP_BLUE} style={{ flexShrink: 0, marginTop: 2 }} />
                  <p className="dash-card-description" style={{ margin: 0 }}>
                    {domain.status === 'purchasing'
                      ? <>{t('publish.registering')}</>
                      : <>{t('publish.awaitingCert')}</>}
                  </p>
                </div>
              ) : needsSetup && domain.diagnosis?.state === 'dns_ok' ? (
                // DNS is already correct — collapse the setup steps, leave only the notice.
                <div className="dash-domain-step" style={{ borderTop: '1px solid var(--dash-border)' }}>
                  <DiagnosisBanner diagnosis={domain.diagnosis} cnameTarget={domain.cnameTarget} standalone />
                </div>
              ) : needsSetup ? (
                <div className="dash-domain-step" style={{ background: stepTint(6), borderTop: `1px solid ${stepTint(18)}` }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <IconInfoCircle size={18} color={STEP_BLUE} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 className="dash-card-primary" style={{ margin: '0 0 14px', fontWeight: 600 }}>{t('publish.completeConnection')}</h4>

                      {domain.diagnosis ? <DiagnosisBanner diagnosis={domain.diagnosis} cnameTarget={domain.cnameTarget} /> : null}

                      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                        <StepNumber n={1} />
                        <p className="dash-card-description" style={{ margin: 0 }}>{t('publish.addRecords')}</p>
                      </div>
                      <div className="dash-domain-step-indent" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {dnsRecords(domain).map((rec, i) => (
                          <div key={`${rec.type}-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 10, borderRadius: 10, border: '1px solid var(--dash-border)', background: 'var(--dash-panel)' }}>
                            <DnsRow label={t('publish.dns.type')} value={rec.type} />
                            <DnsRow label={t('publish.dns.name')} value={rec.name} mono />
                            <DnsRow label={t('publish.dns.value')} value={rec.value} mono />
                          </div>
                        ))}
                        {isApex(domain.hostname) ? (
                          <p className="dash-card-caption" style={{ color: 'var(--dash-muted)', margin: '2px 0 0' }}>{t('publish.rootDomainNote', { hostname: domain.hostname })}</p>
                        ) : null}
                      </div>

                      <div style={{ display: 'flex', gap: 10, margin: '16px 0 12px' }}>
                        <StepNumber n={2} />
                        <p className="dash-card-description" style={{ margin: 0 }}>{t('publish.propagation')}</p>
                      </div>
                      <div className="dash-domain-step-indent" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <button type="button" className="dash-secondary-btn" onClick={() => void retry(domain.id)} disabled={refreshingId === domain.id}>
                          {refreshingId === domain.id
                            ? <><IconLoader2 size={14} className="dash-spin" /> {t('publish.checking')}</>
                            : <><IconRefresh size={14} /> {t('publish.retry')}</>}
                        </button>
                        <span className="dash-card-caption" style={{ color: 'var(--dash-muted)' }}>{t('publish.autoChecked')}</span>
                      </div>

                      {domain.status === 'error' && domain.verification.errors && domain.verification.errors.length > 0 ? (
                        <p className="dash-card-caption dash-domain-step-indent" style={{ marginTop: 12, color: '#f59e0b', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                          <IconAlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} /> {domain.verification.errors.join(' · ')}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </article>
          )
        })}

        </div>
        </div>
      </section>

      {/*
        * Los dominios pagados que no están en ningún sitio.
        *
        * Un dominio es de la CUENTA, no del proyecto: sigue pagado un año
        * aunque se desconecte, y puede acabar en cualquiera de los sitios de la
        * persona. Hasta ahora sólo se veía desde el proyecto del que salió
        * —la lista de arriba consulta por proyecto—, así que quien lo
        * desconectaba para ponerlo en otro se quedaba sin forma de encontrarlo.
        *
        * Va entre la lista y el asistente a propósito: es la respuesta más
        * barata a «quiero un dominio aquí», antes de buscar uno nuevo o de
        * pegar registros de otro registrador. Comprar lo que ya tienes es el
        * error que este bloque evita.
        */}
      {canManageDomains && available.length > 0 ? (
        /* La MISMA caja que «Direcciones activas»: sección, panel blanco con el
           rótulo dentro, y una tarjeta por dominio con su nombre y su acción de
           texto en el mismo renglón. No es parecido a propósito — es que son la
           misma cosa vista dos veces: direcciones del sitio, unas puestas y otras
           disponibles. Con estilos propios se leían como dos productos. */
        <section className="dash-domain-group" aria-label={t('publish.owned.aria')}>
          <div className="dash-domain-panel">
            <h3 className="dash-domain-section-title">{t('publish.owned.title')}</h3>
            <p className="dash-domain-panel-note">
                {t('publish.owned.note')}
            </p>

            {available.map((item) => (
              <article key={item.id} className="dash-domain-card">
                <div className="dash-domain-card-line">
                  <span className="dash-domain-card-name">{item.hostname}</span>
                  <span className="dash-domain-card-connect">
                    {/* El sitio de destino, con éste por defecto: se está aquí y
                        lo normal es querer el dominio aquí. Pero un dominio
                        suelto puede ir a cualquiera, y obligar a cambiar de
                        pantalla para mandarlo a otro era pedir un viaje por una
                        elección que cabe en un desplegable. */}
                    {myProjects.length > 1 ? (
                      <select
                        aria-label={t('publish.siteFor', { domain: item.hostname })}
                        value={target[item.id] ?? projectId}
                        disabled={reconnecting === item.id}
                        onChange={(event) => { setTarget((current) => ({ ...current, [item.id]: event.target.value })) }}
                      >
                        {myProjects.map((project) => (
                          <option key={project.id} value={project.id}>{project.name}</option>
                        ))}
                      </select>
                    ) : null}
                    <button
                      type="button"
                      className="dash-domain-card-change"
                      disabled={reconnecting === item.id}
                      onClick={() => void adopt(item.id, target[item.id] ?? projectId)}
                    >
                      {reconnecting === item.id
                        ? <><IconLoader2 size={13} className="dash-spin" /> {t('publish.connecting')}</>
                        : <><IconLink size={13} aria-hidden="true" /> {t('publish.connect')}</>}
                    </button>
                  </span>
                </div>

                {item.expiresAt ? (
                  <p className="dash-domain-card-renewal">
                    <IconCalendarDollar size={14} aria-hidden="true" />
                    <span>{t('publish.expiresOn', { date: renewalDate(item.expiresAt) })}</span>
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Conseguir un dominio propio ──────────────────────────────────
          Tarjeta aparte que se presenta antes de las direcciones activas.
          Cerrada enseña una sola cosa que hacer; abierta, uno de los dos
          caminos, nunca los dos. */}
      {/* Sin rótulo propio: la tarjeta ya dice de qué va en su título —«Busca
          un dominio para tu sitio» o «Conecta tu dominio»— y encima llevaba otro
          diciendo lo mismo con otras palabras. */}
      {/* Todo el bloque del dominio propio pide `domains.manage`, que sólo tienen
          el propietario y quien administra. Un editor entra aquí —Publicación es
          `project.settings`, y su parte del subdominio sí le toca— pero buscar,
          conectar y comprar le devolvían 403: la pantalla ofrecía tres cosas y
          las tres fallaban al pulsarlas. Se esconde entero en vez de por partes,
          porque las tres cuelgan del mismo permiso. */}
      {canManageDomains ? (
      <section className="dash-domain-group" aria-label={t('publish.search.aria')}>

      {/* Sin tarjeta de presentación delante: era un paso de más que sólo
          decía «pulsa aquí para empezar». Se abre directamente el asistente de
          conectar, que es lo que la mayoría viene a hacer, y desde su pie se
          cambia a buscar uno nuevo. */}
      <div className="dash-domain-hero">
        {/* La cabecera de la pantalla dice de qué va la sección; esto nombra la
            acción concreta que se hace aquí, así que no se pisan. */}
        {/* La mascota, no un icono en una caja de color.
            Y gira mientras se busca: la búsqueda va contra el registrador y
            tarda lo que tarde, así que quien pulsó «Buscar» necesita algo que
            le diga que sigue en marcha. Al acabar se posa derecha y se queda:
            aquí es una insignia del encabezado, no una espera, y por eso lleva
            `restWhenIdle` —sin él se desvanecería como los cargadores y este
            hueco quedaría vacío para siempre. */}
        <span className="dash-domain-hero-badge">
          <LoadingMascot spinning={searching} size={52} restWhenIdle />
        </span>
        <h2 className="dash-domain-hero-title">
          {shopMode === 'search' ? t('publish.hero.searchTitle') : t('publish.hero.connectTitle')}
        </h2>

        {/* El buscador SÓLO cuando se está buscando. Se pintaba siempre, así
            que con el asistente abierto salían el título «Conecta tu dominio» y
            debajo un campo de buscar con su botón: dos cosas distintas pidiendo
            lo mismo, en la misma tarjeta. */}
        {shopMode === 'search' ? (
        <form className="dash-domain-hero-form" onSubmit={(e) => { e.preventDefault(); if (canSearch) void search(`${query}.com`) }}>
          <div
            className="dash-domain-search"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('button, input')) return
              const field = domainSearchInputRef.current
              if (!field) return
              field.focus()
              field.setSelectionRange(field.value.length, field.value.length)
            }}
          >
            <IconSearch size={20} color="var(--dash-muted)" style={{ flexShrink: 0 }} />
            <span className="dash-domain-search-value">
              <input
                ref={domainSearchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(sanitizeDomainName(e.target.value))}
                placeholder={t('publish.search.placeholder')}
                aria-label={t('publish.search.inputAria')}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              <span className="dash-domain-search-suffix" aria-hidden="true">{/* Extensión, no idioma. */}{'.com'}</span>
            </span>
            {/* La cápsula se fue: este botón es el mismo `dash-primary-btn` que
                el resto del panel, y sólo se le dice que no encoja y que crezca
                un punto — el buscador es alto y a 34px quedaba perdido dentro.
                El radio y el relleno los pone su clase, que es lo que hace que
                cambie con los demás el día que cambien. */}
            <button className="dash-primary-btn dash-domain-search-btn" type="submit" disabled={searching || !canSearch}>
              {searching ? <><IconLoader2 size={14} className="dash-spin" /> {t('publish.search.searching')}</> : t('publish.search.button')}
            </button>
          </div>
        </form>

        ) : null}

        {shopMode === 'search' && showResults ? (
          <div className="dash-domain-results">
            {searchError ? (
              <div className="dash-status-label" role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '10px 14px', borderRadius: 10 }}>
                <IconAlertCircle size={15} /> {searchError}
              </div>
            ) : null}

            {/* Comprar en pruebas no compra nada, y callarlo es como acaba
                alguien creyendo que tiene un dominio que nunca se registró. */}
            {results && results.mode !== 'live' ? (
              <p className="dash-card-caption" style={{ margin: 0, color: 'var(--dash-muted)' }}>
                    {t('publish.search.testPrices')}
              </p>
            ) : null}

            {searching && results ? (
              <div key="verifying" className="dash-domain-exact is-verifying" role="status" aria-live="polite" aria-busy="true">
                <div className="dash-domain-exact-identity">
                  <span className="dash-domain-exact-mark"><IconLoader2 size={19} className="dash-spin" /></span>
                  <div style={{ minWidth: 0 }}>
                    <div className="dash-domain-exact-name">{t('publish.search.checking')}</div>
                    <div className="dash-card-caption dash-domain-exact-state">
                      {pendingDomain ? t('publish.search.checkingDomain', { domain: pendingDomain }) : t('publish.search.checkingSelected')}
                    </div>
                  </div>
                </div>
              </div>
            ) : selected ? (
              <div key={selected.domain} className={`dash-domain-exact${selected.available === true ? ' is-available' : ''}${selected.available === false ? ' is-taken' : ''}`}>
                <div className="dash-domain-exact-identity">
                  <span className="dash-domain-exact-mark">
                    {selected.available === true ? <IconCheck size={20} /> : selected.available === false ? <IconX size={19} /> : <IconWorld size={19} />}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="dash-domain-exact-name">{selected.domain}</div>
                    <div className="dash-card-caption dash-domain-exact-state">
                      {selected.available === true
                        ? t('publish.availability.available')
                        : selected.available === null
                          ? t('publish.availability.unknown')
                          : selected.premium
                            ? t('publish.availability.premium')
                            : t('publish.availability.taken')}
                    </div>
                  </div>
                </div>
                <div className="dash-domain-exact-action">
                  {selected.available === true && selected.priceCents !== null ? (
                    <>
                      {/* Cubierto por la membresía anual: el precio se tacha y
                          lo que se lee es «gratis el primer año». Antes esa
                          condición iba en letra pequeña bajo la cifra, así que
                          lo primero —y muchas veces lo único— que se leía era el
                          precio, y quien lo leía se iba a comprarlo por su
                          cuenta a otro sitio. */}
                      {/* Tres respuestas distintas, porque son tres personas
                          distintas mirando. A quien YA tiene crédito se le dice
                          lo que va a pagar; a quien no lo tiene, lo que se
                          ahorraría con el plan anual; a quien ya lo gastó, el
                          precio a secas. Enseñarles a todos «gratis» era lo que
                          acababa en «me dijiste que era gratis y me cobraste». */}
                      {selected.creditAppliedCents > 0 ? (
                        <div className="dash-domain-bump">
                          <span className="dash-domain-bump-tag">
                            {selected.firstYearCents === 0 ? t('publish.bump.freeFirstYear') : t('publish.bump.withCredit')}
                          </span>
                          <span className="dash-domain-bump-price">
                            <s>{money.format(selected.priceCents / 100)}</s>
                            {selected.firstYearCents === 0
                              ? t('publish.bump.coveredByYearly')
                              : t('publish.bump.priceFirstYear', { price: money.format((selected.firstYearCents ?? 0) / 100) })}
                          </span>
                          <span className="dash-domain-bump-note">
                            {t('publish.credit.applied', {
                              amount: money.format(selected.creditAppliedCents / 100),
                              price: money.format(selected.priceCents / 100),
                            })}
                          </span>
                        </div>
                      ) : results?.credit.reason === 'none' && selected.firstYearWithAnnualCents !== null
                          && selected.firstYearWithAnnualCents < selected.priceCents ? (
                        <div className="dash-domain-bump">
                          <span className="dash-domain-bump-tag">
                            {selected.firstYearWithAnnualCents === 0 ? t('publish.bump.freeWithYearly') : t('publish.bump.cheaperWithYearly')}
                          </span>
                          <span className="dash-domain-bump-price">
                            <s>{money.format(selected.priceCents / 100)}</s>
                            {selected.firstYearWithAnnualCents === 0
                              ? t('publish.bump.firstYear')
                              : t('publish.bump.priceFirstYear', { price: money.format(selected.firstYearWithAnnualCents / 100) })}
                          </span>
                          <span className="dash-domain-bump-note">
                            {t('publish.credit.yearlyIncludes', {
                              amount: money.format((results.credit.valueCents ?? 0) / 100),
                              price: money.format(selected.priceCents / 100),
                            })}
                          </span>
                        </div>
                      ) : (
                        <div>
                          <div className="dash-domain-exact-price">{money.format(selected.priceCents / 100)}<span> {t('publish.perYear')}</span></div>
                          <div className="dash-card-caption" style={{ marginTop: 2, color: 'var(--dash-muted)' }}>
                            {results?.credit.reason === 'spent'
                              ? t('publish.creditSpent')
                              : t('publish.needsPaidPlan')}
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        className="dash-primary-btn"
                        onClick={() => { if (isFree) onOpenPlans(); else void buy(selected.domain) }}
                        disabled={buying !== null}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        {buying === selected.domain
                          ? <><IconLoader2 size={14} className="dash-spin" /> {t('publish.openingPayment')}</>
                          : isFree
                            /* Sin plan, lo que se ofrece es el plan, no el
                               dominio: comprarlo suelto deja a alguien con un
                               dominio que todavía no puede conectar. */
                            ? <><IconLock size={14} /> {selected.firstYearWithAnnualCents === 0 ? t('publish.getWithYearly') : t('publish.buyWithPro')}</>
                            : <><IconShoppingCart size={14} /> {t('publish.buyDomain')}</>}
                      </button>
                    </>
                  ) : selected.available === null || (selected.available === true && selected.priceCents === null) ? (
                    /* Libre pero sin precio: el catálogo no llegó a tiempo y se
                       sigue bajando en el servidor. Reintentar es lo que toca;
                       «¿es tuyo?» sería decir que está cogido. */
                    <button type="button" className="dash-secondary-btn" onClick={() => void search(selected.domain)} disabled={searching}>
                      <IconRefresh size={14} /> {t('publish.retry')}
                    </button>
                  ) : (
                    <button type="button" className="dash-secondary-btn" onClick={() => { if (isFree) onOpenPlans(); else openWizard(selected.domain) }}>
                      {isFree ? <><IconLock size={14} /> {t('publish.connectWithPro')}</> : t('publish.isItYours')}
                    </button>
                  )}
                </div>
              </div>
            ) : null}

            {results?.unavailableReason ? (
              <p className="dash-card-caption" style={{ margin: 0, color: '#f59e0b', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <IconAlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} /> {t('publish.unavailableCheck', { reason: results.unavailableReason })}
              </p>
            ) : null}

            {results && results.options.length > 0 ? (
              <>
                <p className="dash-card-caption" style={{ margin: '8px 0 0', color: 'var(--dash-muted)' }}>
                    {t('publish.extensionsFor', { term: results.term })}
                </p>
                {/* El campo guarda el nombre; la extensión viaja solo en la
                    búsqueda, que es lo que la respuesta ya deja claro. */}
                <div className="dash-domain-alts">
                  {results.options.map((option) => {
                    const taken = option.available === false
                    return (
                      <button
                        key={option.domain}
                        type="button"
                        className={[
                          'dash-domain-alt',
                          option.available === true ? 'is-available' : '',
                          taken ? 'is-taken' : '',
                          option.selected ? 'is-selected' : '',
                        ].filter(Boolean).join(' ')}
                        /* Ni un dominio de alguien ni el que ya está arriba
                           llevan a ningún sitio: volver a buscarlos repetiría
                           lo que la pantalla ya está diciendo. */
                        disabled={searching || taken || option.selected}
                        aria-current={option.selected}
                        onClick={() => void search(option.domain)}
                      >
                        <span className="dash-domain-alt-head">
                          <span className="dash-domain-alt-name">{results.term}<b>.{option.tld}</b></span>
                          {option.available === true ? <IconCheck size={15} className="dash-domain-alt-mark" /> : null}
                          {taken ? <IconX size={15} className="dash-domain-alt-mark" /> : null}
                        </span>
                        {/* La misma señal que arriba, en pequeño: si la
                            membresía lo cubre, eso es lo que se lee en vez del
                            precio. Sin esto, la rejilla seguía siendo una lista
                            de precios y la oferta sólo existía para el
                            destacado. */}
                        <span className="dash-domain-alt-price">
                          {taken
                            ? t('publish.alt.unavailable')
                            : option.priceCents === null
                              ? t('publish.alt.checkOnSelect')
                              : option.creditAppliedCents > 0 && option.firstYearCents === 0
                                ? <span className="dash-domain-alt-included">{t('publish.freeWithPlan')}</span>
                                : option.firstYearWithAnnualCents === 0
                                  ? <span className="dash-domain-alt-included">{t('publish.freeWithYearly')}</span>
                                  : t('publish.alt.perYear', { price: money.format(option.priceCents / 100) })}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {/* La otra mitad del público: quien ya tiene dominio no busca, conecta. */}
        {/* Salir del panel, que ahora es lo único que falta aquí: los dos
            caminos —buscar y conectar— los ofrece la lista de arriba, y
            repetirlos dentro obligaba a leer dos veces la misma decisión. */}
        {/* Los pasos van ARRIBA de la línea, no colgando del pie: el pie es
            la salida —cambiar de camino— y lo que se está haciendo tiene que
            estar antes de la puerta, no después. */}
        {wizardStep !== 0 && !isFree ? (
          <div className="dash-domain-wizard">
            {/* Paso 1 — dominio */}
            <div style={{ display: 'flex', gap: 12 }}>
              <StepNumber n={1} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 className="dash-card-primary" style={{ margin: '0 0 8px', fontWeight: 600 }}>{t('publish.wizard.enterDomain')}</h4>
                {wizardStep === 1 ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') goToRegistrar() }}
                        placeholder={t('publish.wizard.domainPlaceholder')}
                        autoFocus
                        style={{ flex: 1, minWidth: 220, height: 42, padding: '0 14px', borderRadius: 9, border: '1px solid var(--dash-border)', background: 'var(--dash-bg)', color: 'var(--dash-text)', fontSize: 14 }}
                      />
                      <button className="dash-primary-btn" type="button" onClick={goToRegistrar} disabled={!input.trim()}>{t('publish.wizard.next')}</button>
                    </div>
                    <p className="dash-card-description" style={{ marginTop: 8 }}>{t('publish.wizard.rootOrSub', { root: 'yourcompany.com', sub: 'www.yourcompany.com' })}</p>
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="dash-card-primary" style={{ fontWeight: 600 }}>{previewHostname}</span>
                    <button type="button" className="dash-ghost-btn" style={{ color: STEP_BLUE, fontWeight: 600, fontSize: 13, padding: 0 }} onClick={() => { setWizardStep(1); setError(null) }}>{t('publish.wizard.change')}</button>
                  </div>
                )}
              </div>
            </div>

            {/* Paso 2 — dónde está registrado */}
            {wizardStep >= 2 ? (
              <div style={{ display: 'flex', gap: 12 }}>
                <StepNumber n={2} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 className="dash-card-primary" style={{ margin: '0 0 4px', fontWeight: 600 }}>{t('publish.wizard.pickProvider')}</h4>
                  <p className="dash-card-description" style={{ margin: '0 0 12px' }}>{t('publish.wizard.pickProviderHint')}</p>
                  {wizardStep === 2 ? (
                    <div className="dash-auto-grid is-tight">
                      {REGISTRARS.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => { setRegistrarId(r.id); setWizardStep(3) }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, borderRadius: 10, border: '1px solid var(--dash-border)', background: 'var(--dash-panel)', cursor: 'pointer', textAlign: 'left', transition: 'border-color 120ms ease, box-shadow 120ms ease' }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = STEP_BLUE }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dash-border)' }}
                        >
                          <BrandLogo name={r.name} color={r.color} domain={r.domain} />
                          <span className="dash-card-meta" style={{ color: 'var(--dash-text)', fontWeight: 600 }}>{r.name}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {registrar ? <BrandLogo name={registrar.name} color={registrar.color} domain={registrar.domain} /> : null}
                      <span className="dash-card-primary" style={{ fontWeight: 600 }}>{registrar?.name}</span>
                      <button type="button" className="dash-ghost-btn" style={{ color: STEP_BLUE, fontWeight: 600, fontSize: 13, padding: 0 }} onClick={() => setWizardStep(2)}>{t('publish.wizard.change')}</button>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Paso 3 — copiar registros + login proveedor */}
            {wizardStep === 3 ? (
              <>
                <div style={{ display: 'flex', gap: 12 }}>
                  <StepNumber n={3} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 className="dash-card-primary" style={{ margin: '0 0 4px', fontWeight: 600 }}>{t('publish.wizard.addRecordAt', { registrar: registrar?.name ?? '' })}</h4>
                    <p className="dash-card-description" style={{ margin: '0 0 12px' }}>{t('publish.wizard.addRecordHint')}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 12, borderRadius: 10, border: '1px solid var(--dash-border)', background: 'var(--dash-bg)' }}>
                      <DnsRow label={t('publish.dns.type')} value={previewRecord.type} />
                      <DnsRow label={t('publish.dns.name')} value={previewRecord.name} mono />
                      <DnsRow label={t('publish.dns.value')} value={previewRecord.value} mono />
                    </div>
                    {isApex(previewHostname) ? (
                      <p className="dash-card-caption" style={{ color: 'var(--dash-muted)', marginTop: 8 }}>{t('publish.rootDomainNote', { hostname: previewHostname })}</p>
                    ) : null}
                    {registrar && registrar.url ? (
                      <a href={registrar.url} target="_blank" rel="noreferrer" className="dash-secondary-btn" style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <IconExternalLink size={14} /> {t('publish.wizard.goTo', { registrar: registrar.name })}
                      </a>
                    ) : null}
                  </div>
                </div>

                {/* Paso 4 — confirmar */}
                <div style={{ display: 'flex', gap: 12 }}>
                  <StepNumber n={4} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 className="dash-card-primary" style={{ margin: '0 0 4px', fontWeight: 600 }}>{t('publish.wizard.verify')}</h4>
                    <p className="dash-card-description" style={{ margin: '0 0 12px' }}>{t('publish.wizard.verifyHint')}</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button className="dash-secondary-btn" type="button" onClick={() => setWizardStep(2)}>
                        <IconArrowLeft size={14} /> {t('publish.wizard.back')}
                      </button>
                      <button className="dash-primary-btn" type="button" onClick={() => void submit()} disabled={adding}>
                        {adding ? <><IconLoader2 size={14} className="dash-spin" /> {t('publish.connecting')}</> : t('publish.wizard.verifyAndConnect')}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {error ? (
              <div className="dash-status-label" role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '10px 14px', borderRadius: 9 }}>
                <IconAlertCircle size={15} /> {error}
              </div>
            ) : null}
          </div>
        ) : null}
        {/* El pie ofrece EL OTRO camino, no el mismo: desde el asistente,
            buscar uno nuevo; desde el buscador, conectar el que ya se tiene.
            Pulsarlo cambia de camino, no abre un segundo formulario. */}
        <div className="dash-domain-hero-foot">
          {shopMode === 'connect' ? (
            <>
              <span className="dash-card-description">{t('publish.noDomainYet')}</span>
              <button className="dash-secondary-btn" type="button" onClick={openShop}>
                <IconSearch size={14} /> {t('publish.findNew')}
              </button>
              {/* Empezar de nuevo, sólo cuando hay algo escrito que perder. La
                  tarjeta ya no se cierra —vive siempre en la página— así que
                  esto devuelve al primer paso en vez de hacerla desaparecer. */}
              {input.trim() || registrarId ? (
                <button className="dash-secondary-btn dash-domain-hero-close" type="button" onClick={requestCancel}>
                  <IconX size={14} /> {t('publish.startOver')}
                </button>
              ) : null}
            </>
          ) : (
            <>
              <span className="dash-card-description">{t('publish.alreadyRegistered')}</span>
              {isFree ? (
                <button className="dash-secondary-btn" type="button" onClick={onOpenPlans}>
                  <IconLock size={14} /> {t('publish.connectWithPro')}
                </button>
              ) : (
                <button className="dash-secondary-btn" type="button" onClick={openConnect}>{t('publish.connectMine')}</button>
              )}
            </>
          )}

        </div>
      </div>
      </section>
      ) : null}
      </div>

      {checkout ? (
        <EmbeddedCheckoutPanel
          clientSecret={checkout.clientSecret}
          summary={checkout.summary}
          /* Cerrar no cancela nada: la sesión sigue viva en Stripe, así que
             volver a comprar abre una nueva en vez de reusar un secreto que
             podría estar ya pagado en otra pestaña. */
          onClose={() => setCheckout(null)}
          /* El dominio no aparece aquí todavía: lo registra el webhook cuando
             Stripe confirma el cobro, y eso tarda unos segundos. La lista se
             recarga y el sondeo hace el resto. */
          onComplete={() => { setCheckout(null); setResults(null); setQuery(''); setJustBought(checkout.domain); void load() }}
        />
      ) : null}

      {justBought ? (
        <DomainPurchaseSuccess
          token={token}
          projectId={projectId}
          domain={justBought}
          onDone={() => { setJustBought(null); void load() }}
        />
      ) : null}

      {confirm?.kind === 'cancel' ? (
        <ConfirmDialog
          title={t('publish.cancelConnection')}
          message={t('publish.cancelMessage')}
          confirmLabel={t('publish.cancelConfirm')}
          cancelLabel={t('publish.cancelDismiss')}
          onConfirm={() => { setConfirm(null); restartWizard() }}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {confirm?.kind === 'disconnect' ? (
        <ConfirmDialog
          title={t('publish.disconnectDomain')}
          /* Dos textos, porque son dos cosas distintas. Uno comprado aquí sigue
             siendo tuyo hasta que venza y se puede volver a conectar; uno
             traído de fuera desaparece de la lista y hay que volver a añadirlo.
             Decirle lo mismo a los dos deja a la mitad creyendo que pierde un
             dominio que pagó. */
          message={confirm.domain.registrar
            ? t('publish.disconnect.ownedMessage', { hostname: confirm.domain.hostname })
            : t('publish.disconnect.externalMessage', { hostname: confirm.domain.hostname })}
          confirmLabel={t('publish.disconnect.confirm')}
          cancelLabel={t('publish.disconnect.cancel')}
          /* Rojo sólo cuando de verdad se pierde algo. Soltar un dominio propio
             es reversible y no borra nada. */
          tone={confirm.domain.registrar ? 'default' : 'danger'}
          onConfirm={() => { const d = confirm.domain; setConfirm(null); void disconnect(d) }}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {/* La MISMA ventana que abre la pantalla de Dominios de la cuenta, no una
          copia: un segundo editor de DNS sería un segundo sitio donde arreglar
          el mismo fallo. Sólo hace falta adaptar el tipo — allí el dominio viene
          con sus datos de cobro y aquí con los del sitio. */}
      {openSettings ? (
        <DomainSettingsModal
          domain={{
            id: openSettings.id,
            hostname: openSettings.hostname,
            projectId,
            projectName,
            status: openSettings.status,
            /* Lo que pagó y cuándo vive en Facturación, no aquí: esta pantalla
               nunca lo pidió. La ventana lo trata como opcional. */
            pricePaidCents: null,
            renewalPriceCents: null,
            currency: 'usd',
            purchasedAt: new Date().toISOString(),
            renewsAt: openSettings.expiresAt,
            sslStatus: openSettings.sslStatus,
          }}
          token={token}
          onClose={() => { setOpenSettings(null); void load() }}
        />
      ) : null}
    </ProjectSettingsLayout>
  )
}
